import React, { memo, useMemo } from "react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { Clock } from "../../../../../icons";
import { FloorplanBooking, ShapeType } from "../floorplanTypes";
import { formatTime, getStageColour, getStageInitial } from "../floorplanUtils";

interface BookingCardProps {
  booking: FloorplanBooking;
  rotation: number;
  shape: ShapeType;
  tableWidth: number;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  rotation,
  shape,
  tableWidth
}) => {
  const { stages } = useDiaryContext();
  const stageColour = useMemo(
    () => getStageColour(stages, booking.currentStage),
    [stages, booking.currentStage]
  );

  const stageInitial = useMemo(
    () => getStageInitial(booking.currentStage),
    [booking.currentStage]
  );

  const showFullTime = tableWidth >= 200;
  const showStartOnly = tableWidth >= 60 && tableWidth < 200;
  const showTimeInside = showFullTime || showStartOnly;
  const showTimes = true;
  const showStage = true;

  const isCircle = shape === "Circle" || shape === "Oval";

  const stageBadgeSize = showTimeInside ? 22 : 26;
  const stageBadgeTextSize = showTimeInside ? "9px" : "10px";

  const { startTime, endTime } = useMemo(
    () => ({
      startTime: formatTime(booking.startTime),
      endTime: formatTime(booking.endTime)
    }),
    [booking.startTime, booking.endTime]
  );

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center p-3 pointer-events-none"
      style={{ transform: `rotate(${-rotation}deg)` }}
    >
      {showTimes && showTimeInside && (
        <div
          className="font-extrabold text-center truncate max-w-full px-2 leading-none"
          style={{
            fontSize: showFullTime ? "12px" : "11px",
            marginTop: isCircle && showStartOnly ? "-4px" : undefined
          }}
        >
          {showFullTime ? `${startTime} - ${endTime}` : startTime}
        </div>
      )}

      {showStage && booking.currentStage && (
        <div
          className="rounded-full flex items-center justify-center font-black relative mt-2"
          style={{
            width: `${stageBadgeSize}px`,
            height: `${stageBadgeSize}px`,
            backgroundColor: stageColour
          }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
          />
          <span
            className="relative z-10 text-black"
            style={{ fontSize: stageBadgeTextSize }}
          >
            {stageInitial ?? <Clock size={16} style={{ color: "black" }} />}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(BookingCard);
